import {Button, Input, Modal, Radio, Spin} from 'antd'
import {
    exam_createExaminationInfo_Api,
    exam_deleteExaminationInfo_Api,
    exam_editExaminationInfo_Api,
    exam_getExaminationInfoAnswers_Api,
} from 'apis'
import {getId} from 'apis/model/base'
import FieldsFactory from 'components/form/FieldsFactory'
import FormActionBar from 'components/form/FormActionBar'
import {FullSideModalLayout} from 'components/Modal/SideModalLayout'
import {Formik, FormikConsumer} from 'formik'
import getTitle from 'helpers/getTitle'
import _ from 'lodash'
import useAsync from 'modules/asyncCache/useAsync'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import Translate from 'modules/local/Translate'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useContext, useEffect, useMemo, useState,} from 'react'
import {useLocation} from 'react-router-dom'
import {withProps} from 'recompose'
import {getResponseItems, NullAsDefault, shallowDiff} from 'views/Shared'
import CRUD from 'views/Shared/enums/CRUD'
import {appFieldTypes} from '../appFieldTypes'
import {appClassNames} from '../custom/appClassNames'
import EntityList from '../EntityList'
import {showDeleteConfirmDialog} from '../factory/createConfirmDialog'
import createContextMenu from '../factory/createContextMenu'
import {notifyOnError} from '../factory/createErrorEvent'
import {createControlledFormField} from '../factory/createFormField'
import {NotificationActionTypes, successNotify,} from '../factory/createNotification'
import {bindQueryParam} from '../functions/routerHelper'
import {ExaminationInfoAnswers, UseExaminationInfoAnswers,} from './components/UseExaminationInfoAnswers'
import {BooleanPicker} from './ExamContext'
import ExaminationInfoAnswersContext, {ExaminationInfoAnswersProvider,} from './ExaminationInfoAnswersContext'
import {QuestionTypes} from './ExaminationPaperContext'
import {BiSolidEdit, BiTrash} from "react-icons/bi";

const ExaminationInfoContext = React.createContext({})

const renderInformationMeta = (item) => {
  const {
    description,
    answer_type,
    is_mandatory,
    examination_info_answers = [],
  } = item ?? {}
  return (
    <Translate>
      {(t) => (
        <div className="flex flex-col p-3 border border-color-50 hover:text-color-100 hover:shadow-items-md rounded-lg relative">
          <div className="flex items-baseline gap-1 leading-tight mb-1">
            <span className="text-sm text-color-400 font-light italic">
              {`${t('ask')}: `}
            </span>
            <span className="text-color-000 font-medium">{getTitle(item)}</span>
          </div>
          {description && (
            <div className="text-sm font-light italic mb-1">{description}</div>
          )}
          <div className="grid grid-cols-2 gap-1">
            <div className="flex items-baseline gap-1 leading-tight text-xs">
              <span className="text-color-400 font-light italic">
                {`${t('mandatory')}: `}
              </span>
              <span className="text-color-100 font-medium">
                {is_mandatory === 1 ? t('yes') : t('no')}
              </span>
            </div>
            <div className="flex items-baseline gap-1 leading-tight text-xs">
              <span className="text-color-400 font-light italic">
                {`${t('answers')}: `}
              </span>
              <span className="text-color-100 font-medium">
                {examination_info_answers.length}
              </span>
            </div>
            <div className="flex items-baseline gap-1 leading-tight text-xs">
              <span className="text-color-400 font-light italic">
                {`${t('answer type')}: `}
              </span>
              <span className="text-color-100 font-medium">
                {t(answer_type)}
              </span>
            </div>
          </div>
        </div>
      )}
    </Translate>
  )
}

const renderInfoAnswerItem =
  ({ renderContextMenu = NullAsDefault }) =>
  (item, index) => {
    const {} = item || {}
    return (
      <Translate key={index}>
        {(t) => (
          <div className="flex flex-col p-2 gap-2 border border-color-50 rounded-md relative">
            <span>{`${index + 1}. ${getTitle(item)}`}</span>
            <div className="absolute right-0 top-0 p-1">
              {renderContextMenu(item)}
            </div>
          </div>
        )}
      </Translate>
    )
  }

const InformationAnswersModal = ({ item, onCancel }) => {
  const t = useTranslate()

  const {
    refreshKey,
    examination_info_id,
    renderContextMenu,
    handleAction = NullAsDefault,
  } = useContext(ExaminationInfoAnswersContext)

  const apiInfo = exam_getExaminationInfoAnswers_Api
  return (
    <div className="flex flex-col p-2">
      {renderInformationMeta(item)}
      <div className="flex items-center justify-between mt-6 mb-3">
        <span className="font-medium text-sm text-color-300 uppercase">
          {t('list of answer')}
        </span>
        <Button
          size="small"
          type="primary"
          className="button-rounded-md no-border font-medium text-xs px-4 leading-tight"
          onClick={() => {
            handleAction(CRUD.CREATE)
          }}>
          {t('add')}
        </Button>
      </div>
      <div className="space-y-3">
        <EntityList
          apiInfo={apiInfo}
          refreshKey={refreshKey}
          RenderEntity={React.Fragment}
          renderItem={renderInfoAnswerItem({
            renderContextMenu,
          })}
          query={bindQueryParam({ id: examination_info_id })}
        />
      </div>
    </div>
  )
}

const AnswerOptions = ({
  name,
  value,
  form,
  onChange = NullAsDefault,
  disabled = false,
  options = [
    QuestionTypes.TEXT,
    QuestionTypes.BOOL,
    QuestionTypes.SINGLE,
    QuestionTypes.MULTIPLE,
  ],
  answers_field = 'examination_info_answers',
}) => {
  const t = useTranslate()
  const { setFieldValue } = form || {}
  const handleChange = (event) => {
    onChange(event)
    let answers = []
    switch (event.target.value) {
      case QuestionTypes.TEXT.value:
      case QuestionTypes.SINGLE.value:
        answers = [{ title: '' }]
        break
      case QuestionTypes.MULTIPLE.value:
        answers = [{ title: '' }, { title: '' }]
        break
      case QuestionTypes.BOOL.value:
        answers = appFieldTypes.yesOrno.map(({ label, value }) => ({
          title: t(label),
        }))
        break
      default:
        break
    }
    setFieldValue(
      answers_field,
      answers.map((answer, index) => ({
        ...answer,
        order_sort: index,
        id: _.uniqueId('__id_' + index),
      }))
    )
  }
  return (
    <div className="w-full">
      <Radio.Group
        name={name}
        value={value}
        optionType="button"
        buttonStyle="solid"
        disabled={disabled}
        onChange={handleChange}
        className="custom-radio-button">
        {options.map(({ label, value }) => (
          <Radio
            key={value}
            value={value}>
            {t(label)}
          </Radio>
        ))}
      </Radio.Group>
    </div>
  )
}

const formSchema = {
  title: {
    title: 'title',
    children: [
      {
        name: 'title',
        component: createControlledFormField({
          placeholder: 'title',
        }),
      },
    ],
  },
  desscription: {
    title: 'description',
    children: [
      {
        name: 'description',
        component: createControlledFormField({
          rows: 3,
          placeholder: 'description',
          Component: Input.TextArea,
        }),
      },
    ],
  },
  create_answer_type: {
    children: [
      {
        name: 'answer_type',
        component: AnswerOptions,
      },
    ],
  },
  edit_answer_type: {
    children: [
      {
        name: 'answer_type',
        disabled: true,
        component: AnswerOptions,
      },
    ],
  },
  boolean_1: {
    inline: true,
    className: appClassNames.inline_2,
    children: [
      {
        name: 'is_mandatory',
        label: 'mandatory',
        component: BooleanPicker,
      },
      {
        name: 'is_fixed_orders',
        label: 'fixed orders',
        component: BooleanPicker,
      },
    ],
  },
  create_question_answers: {
    children: [
      {
        name: 'examination_info_answers',
        component: withProps(({ form, name, value, onChange }) => ({
          name,
          value,
          onChange,
          answer_type: form?.values?.answer_type,
        }))(ExaminationInfoAnswers),
      },
    ],
  },
  edit_question_answers: {
    children: {
      name: 'examination_info_answers',
      component: ({
        name,
        value,
        refresh,
        onChange,
        answer_type,
        examination_id,
        examination_info_id,
      }) => (
        <UseExaminationInfoAnswers
          refresh={refresh}
          examination_info_id={examination_info_id}>
          {(props) => (
            <ExaminationInfoAnswers
              {...props}
              {...{
                name,
                value,
                onChange,
                answer_type,
                editable: true,
                examination_id,
                examination_info_id,
              }}
            />
          )}
        </UseExaminationInfoAnswers>
      ),
    },
  },
}

const CreateInformationModal = ({
  initialValues,
  examination_id,
  onSubmit = NullAsDefault,
}) => {
  const t = useTranslate()

  const _initialValues = useMemo(() => {
    return {
      is_mandatory: 0,
      is_fixed_orders: 1,
      answer_type: QuestionTypes.TEXT.value,
      examination_info_answers: [{ title: '', id: _.uniqueId('__id_') }],
      ...(initialValues ?? {}),
    }
  }, [initialValues])

  const validate = (values) => {
    const { title, answer_type, examination_info_answers } = values || {}

    let errors = {}

    if (_.isEmpty(title)) {
      errors.title = t('required field')
    }

    if (_.isEmpty(answer_type)) {
      errors.answer_type = t('required field')
    }

    if (
      _.some(examination_info_answers, ({ title }) => _.isEmpty(title)) &&
      answer_type !== QuestionTypes.TEXT.value
    ) {
      errors.answers_error_container = t('required field')
    }

    return errors
  }

  return (
    <Formik
      validate={validate}
      initialValues={_initialValues}
      onSubmit={(values) => {
        const { examination_info_answers, ...rest } = values ?? {}
        onSubmit(
          _.omitBy(
            {
              answer_type: values?.answer_type ?? undefined,
              ...rest,
              examination_info_answers: _.isEmpty(examination_info_answers)
                ? undefined
                : JSON.stringify(
                    Array.from(examination_info_answers).map(
                      ({ id, ...item }, index) => ({
                        ...item,
                        order_sort: index,
                      })
                    )
                  ),
            },
            _.isUndefined
          )
        )
      }}>
      <div className="flex flex-col gap-3">
        <FieldsFactory
          formSchema={[
            formSchema.create_answer_type,
            formSchema.title,
            formSchema.create_question_answers,
            formSchema.desscription,
            formSchema.boolean_1,
          ]}
        />
        <FormActionBar submitText="create" />
      </div>
    </Formik>
  )
}

const EditInformationModal = ({
  examination_id,
  initialValues: item,
  onSubmit = NullAsDefault,
}) => {
  const t = useTranslate()

  const location = useLocation()

  const refreshKey = location?.state?.refreshToken

  const examination_info_id = getId(item)

  const {
    response,
    isLoading = false,
    handleAsyncAction,
  } = useAsync({
    query: bindQueryParam({ id: examination_info_id }),
    apiInfo: exam_getExaminationInfoAnswers_Api,
  })

  useEffect(() => {
    if (refreshKey) {
      handleAsyncAction()
    }
  }, [refreshKey])

  const initialValues = useMemo(
    () => ({
      ...item,
      refreshKey,
      examination_info_answers: getResponseItems(response),
    }),
    [item, response, refreshKey]
  )

  const validate = (values) => {
    const { title, answer_type } = values || {}

    let errors = {}

    if (_.isEmpty(title)) {
      errors.title = t('required field')
    }

    if (_.isEmpty(answer_type)) {
      errors.answer_type = t('required field')
    }

    return errors
  }

  return (
    <Formik
      validate={validate}
      enableReinitialize={true}
      initialValues={initialValues}
      onSubmit={(values) => {
        const { examination_info_answers, ...rest } = values || {}
        const changed = shallowDiff(rest, initialValues)
        onSubmit(
          _.omitBy(
            {
              answer_type: values?.answer_type || undefined,
              ...changed,
            },
            _.isUndefined
          )
        )
      }}>
      <div className="flex flex-col gap-3">
        <FieldsFactory
          formSchema={[formSchema.edit_answer_type, formSchema.title]}
        />
        <FormikConsumer>
          {({ values, handleChange }) => {
            const { answer_type } = values || {}
            const { name, component: Component } =
              formSchema.edit_question_answers.children
            return (
              <Spin spinning={!!isLoading}>
                <Component
                  {...{
                    name,
                    answer_type,
                    examination_id,
                    examination_info_id,
                    value: _.get(values, name),
                    refresh: ({ history }) => {
                      if (history) {
                        history.push({
                          ...(history?.location || {}),
                          state: {
                            ...(history?.location?.state || {}),
                            refreshToken: Date.now(),
                          },
                        })
                      }
                    },
                    onChange: handleChange,
                  }}
                />
              </Spin>
            )
          }}
        </FormikConsumer>
        <FieldsFactory
          formSchema={[formSchema.desscription, formSchema.boolean_1]}
        />
        <FormActionBar submitText="save" />
      </div>
    </Formik>
  )
}

export const ExaminationInfoProvider = ({
  examination_id,
  children,
  ...props
}) => {
  const t = useTranslate()

  const [action, setAction] = useState({
    type: undefined,
    value: undefined,
  })

  const [refreshKey, setRefreshKey] = useState()

  const onError = notifyOnError(t)

  const onCancel = () => {
    setAction({})
  }

  const reload = () => {
    setRefreshKey(Date.now())
    onCancel()
  }

  const { handleAsyncAction: onCreate } = useAsyncAction({
    apiInfo: exam_createExaminationInfo_Api,
    query: bindQueryParam({ id: examination_id }),
    onError,
    onSuccess: () => {
      successNotify(NotificationActionTypes.CREATE, t)
      reload()
    },
  })

  const { handleAsyncAction: onUpdate } = useAsyncAction({
    apiInfo: exam_editExaminationInfo_Api,
    onError,
    onSuccess: () => {
      successNotify(NotificationActionTypes.UPDATE, t)
      reload()
    },
  })

  const { handleAsyncAction: onDelete } = useAsyncAction({
    apiInfo: exam_deleteExaminationInfo_Api,
    onError,
    onSuccess: () => {
      successNotify(NotificationActionTypes.DELETE, t)
      reload()
    },
  })

  const confirmDelete = useCallback(
    (id) => {
      if (id) {
        showDeleteConfirmDialog({
          translate: t,
          title: 'delete question',
          message: 'are you sure you want to delete it question',
          onOk: () => {
            onDelete({}, { id })
          },
        })
      }
    },
    [onDelete]
  )

  const handleAction = (action, params) => {
    const { id } = params || {}
    switch (action) {
      case CRUD.READ:
      case CRUD.CREATE:
      case CRUD.UPDATE:
        setAction({ type: action, value: params })
        break
      case CRUD.DELETE:
        confirmDelete(id)
        break
      default:
        break
    }
  }

  const renderContextMenu = useCallback(
    (item) =>
      createContextMenu(
        [
          {
            icon: <BiSolidEdit size={14} className="text-blue-500"/>,
            label: 'edit',
            key: CRUD.UPDATE,
          },
          {
            icon: <BiTrash size={14} className="text-primary-300"/>,
            label: 'delete',
            key: CRUD.DELETE,
          },
        ].filter((e) => item),
        (key) => {
          handleAction(key, item)
        }
      ),
    []
  )

  const modal = useMemo(() => {
    const { id } = action.value || {}
    let title
    let Content = NullAsDefault
    let params = { title: undefined }
    switch (action.type) {
      case CRUD.READ:
        return (
          <FullSideModalLayout
            right={true}
            title={
              <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                {t('answers')}
              </div>
            }
            onCancel={onCancel}>
            <ExaminationInfoAnswersProvider examination_info_id={id}>
              <InformationAnswersModal
                item={action.value}
                {...{ onCancel }}
              />
            </ExaminationInfoAnswersProvider>
          </FullSideModalLayout>
        )
      case CRUD.CREATE:
        title = (
          <div className="text-center font-bold text-color-000 uppercase tracking-wide">
            {t('create question')}
          </div>
        )
        params = {
          onCancel,
          onSubmit: (values) => {
            onCreate(values)
          },
        }
        Content = CreateInformationModal
        break
      case CRUD.UPDATE:
        title = (
          <div className="text-center font-bold text-color-000 uppercase tracking-wide">
            {t('edit question')}
          </div>
        )
        params = {
          onCancel,
          onSubmit: (values) => {
            onUpdate(values, { id })
          },
        }
        Content = EditInformationModal
        break
      default:
        break
    }
    return (
      <Modal
        className="custom-modal"
        footer={null}
        title={title}
        onCancel={onCancel}
        open={action.type}
        destroyOnHidden={true}>
        <Content
          {...params}
          examination_id={examination_id}
          initialValues={action.value || {}}
        />
      </Modal>
    )
  }, [action])

  return (
    <ExaminationInfoContext.Provider
      key={examination_id}
      value={{
        action,
        refreshKey,
        onCreate,
        onUpdate,
        onDelete,
        handleAction,
        examination_id,
        renderContextMenu,
        ...props,
      }}>
      <React.Fragment>
        {children}
        {modal}
      </React.Fragment>
    </ExaminationInfoContext.Provider>
  )
}

export default ExaminationInfoContext
